import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useApolloClient, useMutation, gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';
import { BellIcon, UserCircleIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon, UserIcon, PresentationChartBarIcon, ChevronRightIcon, ArrowPathIcon, BeakerIcon, RocketLaunchIcon, CheckCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import AppLauncher from './AppLauncher';
import GettingStartedDrawer from '../GettingStarted/GettingStartedDrawer';
import ThemeToggle from './ThemeToggle';
import SuccessionNotificationsContainer from './Notifications/containers/SuccessionNotificationsContainer';
import { clearTourKeys } from '../Shared/GuidedTour/tourKeys';
import { getAvatarStyle } from '../../lib/s3';
const COACH_PROFILE_QUERY = gql`
  query CoachProfile {
    coachProfile {
      id
    }
  }
`;

const RESET_DEMO_DATA = gql`
  mutation ResetDemoData($resetType: String!) {
    resetDemoData(resetType: $resetType) {
      success
      errors
    }
  }
`;

const SEED_DEMO_DATA = gql`
  mutation SeedDemoData($seedType: String!, $skipLast: Int) {
    seedDemoData(seedType: $seedType, skipLast: $skipLast) {
      success
      errors
      async
    }
  }
`;

const DEMO_SEED_STATUS_QUERY = gql`
  query DemoSeedStatus {
    demoSeedStatus {
      status
      seedType
      message
    }
  }
`;

const PLAN_TEMPLATE_TOTAL_QUERY = gql`
  query PlanTemplateTotalQuestions($slug: String!) {
    planTemplate(slug: $slug) {
      id
      total_questions
    }
  }
`;

const DEMO_RESET_OPTIONS = [
  { key: 'all', label: 'Reset All' },
  { key: 'onboarding', label: 'Reset Onboarding' },
  { key: 'valuation_studio', label: 'Reset Valuation Studio' },
  { key: 'deal_room', label: 'Reset Deal Room' },
  { key: 'buyer_deal_room', label: 'Reset Buyer Deal Room' },
  { key: 'plan_builder', label: 'Reset PlanBuilder' },
  { key: 'readiness_assessment', label: 'Reset Readiness Assessment' },
  { key: 'value_builder', label: 'Reset ValueBuilder' },
  { key: 'cash_flows', label: 'Reset Cash Flows' },
  { key: 'conversations', label: 'Reset Conversations' },
  { key: 'rolodex', label: 'Reset Rolodex' },
  { key: 'search', label: 'Reset Search' },
  { key: 'vault', label: 'Reset Vault' },
  { key: 'education', label: 'Reset Education' },
  { key: 'consultation', label: 'Reset Consultation' },
  { key: 'seller_profile', label: 'Reset Seller Profile' },
  { key: 'buyer_profile', label: 'Reset Buyer Profile' },
  { key: 'recruitment', label: 'Reset Recruitment' },
  { key: 'sprint_programs', label: 'Reset Sprint Programs' },
];

const DEMO_SEED_OPTIONS = [
  { key: 'seller_deal_room', label: 'Seed Seller Deal Room', requiresMotivation: 'succession', description: 'This will seed a seller deal room scenario with fake buyers for demonstration/testing.' },
  { key: 'buyer_deal_room', label: 'Seed Buyer Deal Room', requiresMotivation: 'growth', description: 'This will seed a buyer deal room scenario where you are invited to a deal as a growth-motivated buyer. A fake seller and other fake buyers will be created.' },
  { key: 'plan_near_complete', label: 'Seed Plan (Near-Complete)', description: 'This will seed the Succession Plan with answers to all but the last 2 questions, simulating a near-complete plan.' },
  { key: 'plan_mid', label: 'Seed Plan (Mid-Plan)', needsSkipLastInput: true, templateSlug: 'comprehensive-succession-plan-v2', description: 'This will seed the Succession Plan, skipping questions from the end. You choose how many to skip.' },
  { key: 'cat_plan_near_complete', label: 'Seed Cat Plan (Near-Complete)', description: 'This will seed the Catastrophic Plan with answers to all but the last 2 questions, simulating a near-complete plan.' },
  { key: 'cat_plan_mid', label: 'Seed Cat Plan (Mid-Plan)', needsSkipLastInput: true, templateSlug: 'catastrophic-plan-v1', description: 'This will seed the Catastrophic Plan, skipping questions from the end. You choose how many to skip.' },
  { key: 'pilot_help_center', label: 'Seed Pilot Help Center', description: 'Seeds pilot-specific help articles (platform overview, glossary, success criteria). Idempotent — safe to run multiple times.' },
  { key: 'recruitment', label: 'Seed Recruitment', description: 'Creates 10 hiring posts for @findbob.io advisors and 15 student accounts with @findbob.edu emails. Idempotent — safe to run multiple times.' },
  { key: 'sprint_programs', label: 'Seed Sprint Programs', description: 'Enrolls up to 12 @findbob.io users in each published sprint program with simulated progress and coach–participant comment threads.' },
];

const DEMO_SCENARIOS = [
  {
    key: 'seller_scenario_1',
    label: 'Seller Scenario #1',
    asyncSeedType: 'scenario_seller_1', // Single Sidekiq job runs all steps server-side
    description: 'Sets up a complete seller demonstration environment. This is the recommended starting point for a moderated pilot session with a seller persona.',
    steps: [
      { label: 'Reset All', description: 'Clears all demo data including onboarding, valuations, deals, plans, the readiness assessment, cash flows, conversations, and rolodex.' },
      { label: 'Reset Seller Profile', description: 'Seeds seller persona — 25 years experience, $298M AUM, succession motivation.' },
      { label: 'Seed Seller Deal Room', description: 'Creates a published deal with ~25 buyers at various workflow stages.' },
      { label: 'Seed Plan (Mid-Plan)', description: 'Seeds a partially completed succession plan (~50% progress).' },
      { label: 'Seed Pilot Help Center', description: 'Adds pilot guide articles to the Help Center and Support Drawer.' },
    ],
  },
  {
    key: 'buyer_scenario_1',
    label: 'Buyer Scenario #1',
    asyncSeedType: 'scenario_buyer_1',
    description: 'Sets up a complete buyer demonstration environment. Use this for a moderated pilot session with a buyer/growth persona.',
    steps: [
      { label: 'Reset All', description: 'Clears all demo data including onboarding, valuations, deals, plans, the readiness assessment, cash flows, conversations, and rolodex.' },
      { label: 'Reset Buyer Profile', description: 'Seeds buyer persona — 10 years experience, $99M AUM, growth motivation.' },
      { label: 'Seed Buyer Deal Room', description: 'Creates a buyer deal room with ~25 seller deals at various workflow stages.' },
      { label: 'Seed Plan (Mid-Plan)', description: 'Seeds a partially completed succession plan (~50% progress).' },
      { label: 'Seed Pilot Help Center', description: 'Adds pilot guide articles to the Help Center and Support Drawer.' },
    ],
  },
];

// Lightweight progress donut for header indicator
const ProgressDonut = ({ percent = 0, size = 18, stroke = 3, color = '#ffffff', bg = 'rgba(255,255,255,0.35)' }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));
  const dash = (clamped / 100) * circumference;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={bg} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={stroke} strokeDasharray={`${dash} ${circumference - dash}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`} />
    </svg>
  );
};

// Try to import SuccessionThemeProvider, fall back gracefully if not available
let useSuccessionTheme;
try {
  const SuccessionThemeModule = require('./SuccessionThemeProvider');
  useSuccessionTheme = SuccessionThemeModule.useSuccessionTheme;
} catch (error) {
  // SuccessionThemeProvider not available, we'll handle this gracefully
  useSuccessionTheme = null;
}

const Header = ({ user, company: companyProp, features, logoLight, logoDark, demoSecretMenuEnabled, quickbooksConnectorEnabled }) => {
  const intl = useIntl();
  // Preserve avatar settings from original prop (theme context may not have them)
  const avatarSettings = {
    avatar_background_color: companyProp?.avatar_background_color,
    avatar_directory: companyProp?.avatar_directory,
  };
  
  const [isGsOpen, setIsGsOpen] = useState(false);
  const [gsPercent, setGsPercent] = useState(0);
  const [demoConfirm, setDemoConfirm] = useState(null); // { key, label } or null
  const [demoStatus, setDemoStatus] = useState(null); // { type: 'success'|'error', message } or null
  const [demoResetExpanded, setDemoResetExpanded] = useState(false);
  const [demoSeedExpanded, setDemoSeedExpanded] = useState(false);
  const [demoSeedConfirm, setDemoSeedConfirm] = useState(null); // { key, label, description } or null
  const [demoOperationInProgress, setDemoOperationInProgress] = useState(null); // { message } or null
  const [midPlanSkipLast, setMidPlanSkipLast] = useState(''); // skip_last value for mid-plan seed
  const [midPlanTotalQuestions, setMidPlanTotalQuestions] = useState(null); // total questions from template
  const [demoScenariosExpanded, setDemoScenariosExpanded] = useState(false); // scenarios sub-menu
  const [scenarioConfirm, setScenarioConfirm] = useState(null); // scenario to confirm { key, label, steps, ... }
  const [scenarioProgress, setScenarioProgress] = useState(null); // { steps: [{label, status}], currentStep }
  // Local state for theme that syncs with global theme
  const [localTheme, setLocalTheme] = useState('light');
  const [localIsDarkTheme, setLocalIsDarkTheme] = useState(false);

  // Check if user has a coach profile
  const { data: coachProfileData } = useQuery(COACH_PROFILE_QUERY, {
    skip: !user?.id,
    errorPolicy: 'ignore',
  });
  const hasCoachProfile = !!coachProfileData?.coachProfile;

  const [resetDemoData, { loading: resetLoading }] = useMutation(RESET_DEMO_DATA);
  const [seedDemoData, { loading: seedLoading }] = useMutation(SEED_DEMO_DATA);
  const apolloClient = useApolloClient();

  const handleDemoReset = async (resetType) => {
    const label = DEMO_RESET_OPTIONS.find((o) => o.key === resetType)?.label || 'Resetting...';
    setDemoConfirm(null);
    setDemoStatus(null);
    setDemoOperationInProgress({ message: `${label}... This may take a moment.` });
    try {
      const { data } = await resetDemoData({ variables: { resetType } });
      if (data?.resetDemoData?.success) {
        clearTourKeys('all');
        setDemoOperationInProgress({ message: 'Reset completed successfully! Redirecting to Launchpad...' });
        setTimeout(() => { window.location.href = '/succession/launchpad'; }, 1200);
      } else {
        const errorMsg = data?.resetDemoData?.errors?.join(', ') || 'Unknown error';
        setDemoOperationInProgress(null);
        setDemoStatus({ type: 'error', message: errorMsg });
        setTimeout(() => setDemoStatus(null), 5000);
      }
    } catch (err) {
      setDemoOperationInProgress(null);
      setDemoStatus({ type: 'error', message: err.message || 'An error occurred' });
      setTimeout(() => setDemoStatus(null), 5000);
    }
  };

  // Ref to hold the polling interval so we can clean it up
  const seedPollRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (seedPollRef.current) clearInterval(seedPollRef.current);
    };
  }, []);

  const startSeedPolling = (label) => {
    seedPollRef.current = setInterval(async () => {
      try {
        const { data: pollData } = await apolloClient.query({
          query: DEMO_SEED_STATUS_QUERY,
          fetchPolicy: 'network-only',
        });
        const status = pollData?.demoSeedStatus;
        if (!status) return; // cache expired or not yet written

        if (status.status === 'completed') {
          clearInterval(seedPollRef.current);
          seedPollRef.current = null;
          setDemoOperationInProgress({ message: 'Seed completed successfully! Redirecting to Launchpad...' });
          setTimeout(() => { window.location.href = '/succession/launchpad'; }, 1200);
        } else if (status.status === 'failed') {
          clearInterval(seedPollRef.current);
          seedPollRef.current = null;
          setDemoOperationInProgress(null);
          setDemoStatus({ type: 'error', message: status.message || 'Seed failed' });
          setTimeout(() => setDemoStatus(null), 8000);
        } else {
          // still queued or running – update message
          setDemoOperationInProgress({ message: `${label}... ${status.message || 'This may take a moment.'}` });
        }
      } catch (_pollErr) {
        // Silently retry on next interval; transient network blips are expected
      }
    }, 1500);
  };

  const handleDemoSeed = async (option, extraVars = {}) => {
    setDemoSeedConfirm(null);
    setMidPlanSkipLast('');
    setMidPlanTotalQuestions(null);
    setDemoStatus(null);

    // Check motivation requirement
    if (option.requiresMotivation && user.motivation !== option.requiresMotivation) {
      setDemoStatus({ type: 'error', message: `This action is only available for ${option.requiresMotivation}-motivated users. Current motivation: ${user.motivation || 'not set'}.` });
      setTimeout(() => setDemoStatus(null), 5000);
      return;
    }

    setDemoOperationInProgress({ message: `${option.label}... This may take a moment.` });
    try {
      const variables = { seedType: option.key, ...extraVars };
      const { data } = await seedDemoData({ variables });
      if (data?.seedDemoData?.success) {
        if (data.seedDemoData.async) {
          // Operation dispatched to background – start polling
          startSeedPolling(option.label);
        } else {
          // Synchronous operation completed immediately
          setDemoOperationInProgress({ message: 'Seed completed successfully! Redirecting to Launchpad...' });
          setTimeout(() => { window.location.href = '/succession/launchpad'; }, 1200);
        }
      } else {
        const errorMsg = data?.seedDemoData?.errors?.join(', ') || 'Unknown error';
        setDemoOperationInProgress(null);
        setDemoStatus({ type: 'error', message: errorMsg });
        setTimeout(() => setDemoStatus(null), 5000);
      }
    } catch (err) {
      setDemoOperationInProgress(null);
      setDemoStatus({ type: 'error', message: err.message || 'An error occurred' });
      setTimeout(() => setDemoStatus(null), 5000);
    }
  };

  // --- Scenario orchestration ---
  // The entire scenario runs as a single Sidekiq job on the backend, so all steps
  // (resets + seeds) execute sequentially on the same DB connection / user object.
  // The frontend just dispatches once and polls for progress.

  const pollScenarioUntilComplete = (scenario, stepStates) => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        try {
          const { data: pollData } = await apolloClient.query({
            query: DEMO_SEED_STATUS_QUERY,
            fetchPolicy: 'network-only',
          });
          const status = pollData?.demoSeedStatus;
          if (!status) return;

          if (status.status === 'completed') {
            clearInterval(interval);
            // Mark all steps done
            stepStates.forEach((s) => { s.status = 'done'; });
            setScenarioProgress({ steps: [...stepStates], currentStep: scenario.steps.length - 1 });
            resolve();
          } else if (status.status === 'failed') {
            clearInterval(interval);
            reject(new Error(status.message || 'Scenario failed'));
          } else {
            // Parse "Step X/Y:" from worker progress messages to update step checklist
            const match = status.message?.match(/^Step (\d+)\/(\d+)/);
            if (match) {
              const currentStep = parseInt(match[1], 10) - 1; // 0-based
              stepStates.forEach((s, idx) => {
                if (idx < currentStep) s.status = 'done';
                else if (idx === currentStep) s.status = 'running';
                else s.status = 'pending';
              });
              setScenarioProgress({ steps: [...stepStates], currentStep });
            }
            setDemoOperationInProgress({ message: status.message || 'Processing...' });
          }
        } catch (_pollErr) {
          // Silently retry on next interval
        }
      }, 1500);
    });
  };

  const executeScenario = async (scenario) => {
    setScenarioConfirm(null);
    setIsDropdownOpen(false);
    setDemoStatus(null);

    const stepStates = scenario.steps.map((s) => ({ label: s.label, status: 'pending' }));
    setScenarioProgress({ steps: stepStates, currentStep: 0 });
    setDemoOperationInProgress({ message: `Starting ${scenario.label}...` });

    try {
      // Dispatch the entire scenario as a single async seed type
      const { data } = await seedDemoData({ variables: { seedType: scenario.asyncSeedType } });
      if (!data?.seedDemoData?.success) {
        throw new Error(data?.seedDemoData?.errors?.join(', ') || 'Scenario dispatch failed');
      }

      // Poll until the worker reports completion
      if (data.seedDemoData.async) {
        await pollScenarioUntilComplete(scenario, stepStates);
      }

      // Clear tour keys client-side (scenario includes reset_all)
      clearTourKeys('all');

      setDemoOperationInProgress({ message: `${scenario.label} complete! Redirecting to Launchpad...` });
      setTimeout(() => {
        setScenarioProgress(null);
        window.location.href = '/succession/launchpad';
      }, 800);
    } catch (err) {
      setDemoOperationInProgress(null);
      setScenarioProgress(null);
      setDemoStatus({ type: 'error', message: `${scenario.label} failed: ${err.message}` });
      setTimeout(() => setDemoStatus(null), 8000);
    }
  };

  // Function to read theme from document and localStorage
  const readThemeFromDOM = () => {
    const savedTheme = localStorage.getItem('succession-theme') || 'light';
    const isDark = savedTheme === 'dark';
    const hasThemeClass = document.documentElement.classList.contains(`succession-${savedTheme}`);
    
    setLocalTheme(savedTheme);
    setLocalIsDarkTheme(isDark);
    return { theme: savedTheme, isDarkTheme: isDark };
  };

  // Initialize theme on mount and listen for changes
  useEffect(() => {
    // Read initial theme
    readThemeFromDOM();

    // Listen for succession theme change events
    const handleThemeChange = (e) => {
      console.log('Header: Received theme change event:', e.detail);
      const newTheme = e.detail.theme;
      setLocalTheme(newTheme);
      setLocalIsDarkTheme(newTheme === 'dark');
    };

    // Listen for localStorage changes (from other tabs)
    const handleStorageChange = (e) => {
      if (e.key === 'succession-theme') {
        console.log('Header: Received storage change:', e.newValue);
        readThemeFromDOM();
      }
    };

    window.addEventListener('succession-theme-change', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);

    // Also check for DOM mutations in case theme classes are updated
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const classList = Array.from(document.documentElement.classList);
          if (classList.some(cls => cls.startsWith('succession-'))) {
            readThemeFromDOM();
          }
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      window.removeEventListener('succession-theme-change', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const handler = () => setIsGsOpen(true);
    window.addEventListener('open-getting-started', handler);
    return () => window.removeEventListener('open-getting-started', handler);
  }, []);

  // Do not auto-open GettingStarted after onboarding has completed
  useEffect(() => {
    try {
      const tourCompleted = localStorage.getItem('succession_home_tour_completed') === 'true' || localStorage.getItem('succession_home_tour_shown_v1') === 'true';
      if (tourCompleted) {
        setIsGsOpen(false);
      }
    } catch (_) {}
  }, [user?.id]);

  useEffect(() => {
    try {
      const featuresArr = features || [];
      const result = (function compute() {
        const flagEnabled = Array.isArray(featuresArr) ? featuresArr.includes('triggering_event_getting_started') : false;
        const events = Array.isArray(user.triggering_events) ? user.triggering_events : [];
        const hasEvent = (type) => events.some((e) => e.event_type === type);
        const isDone = (id) => {
          switch(id){
            case 'find_my_gaps': {
              const explicit = user['has_completed_triggering_events?'];
              if (explicit !== undefined && explicit !== null) return Boolean(explicit);
              if (user.has_completed_triggering_events !== undefined) return Boolean(user.has_completed_triggering_events);
              const evts = Array.isArray(user.triggering_events) ? user.triggering_events : [];
              return evts.some((e) => e && e.completed_at);
            }
            case 'valuation': return user.valuation_last_accessed_date !== null;
            case 'profile': return user.profile_last_accessed_date !== null;
            case 'discovery': return user['has_used_discovery?'];
            case 'bcp': return user?.bcp_form_datum && (user.bcp_form_datum.status === 'in_progress' || user.bcp_form_datum.status === 'completed');
            case 'education': return user['has_viewed_education?'];
            case 'owner_diagnostic': return flagEnabled ? hasEvent('owner_diagnostic') : user.diag_completed_at != null;
            case 'profit_gap': {
              if (flagEnabled) return hasEvent('profit_gap');
              const isWealth = (company && company.business_type) === 'wealth_rj';
              if (user.profit_gap_recasted_ebitda != null) return true;
              if (isWealth) return user.gross_production != null && user.normalized_operating_expenses != null;
              return user.recurring_revenue != null && user.normalized_operating_expenses != null;
            }
            case 'value_gap': {
              const wg = isDone('wealth_gap');
              const pg = isDone('profit_gap');
              const od = isDone('owner_diagnostic');
              return wg && pg && od && user.value_gap_current_value !== null && user.value_gap_gap_value !== null;
            }
            case 'consultation': return user.consultation_booked_at !== null;
            case 'deal_room': return user.inventory_user && user.inventory_user.deals_count > 0;
            case 'wealth_gap': return flagEnabled ? hasEvent('wealth_gap') : (user['has_wealth_gap_data?'] === true || user.has_wealth_gap_data === true);
            default: return false;
          }
        };
        const defaults = ['consultation','profile','valuation','bcp','deal_room','education','discovery'];
        let list = defaults;
        if (Array.isArray(user.suggested_activities) && user.suggested_activities.length > 0) {
          list = user.suggested_activities;
        } else if (user.motivation === 'succession') {
          list = ['consultation','profile','valuation','bcp','deal_room','education','catastrophe','find_my_gaps'];
        }
        const acts = (list || []).map((id) => ({ id }));
        const total = acts.length || 0;
        const done = acts.filter((a) => isDone(a.id)).length;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;
        return { activities: acts, isActivityCompleted: isDone, pct };
      })();
      setGsPercent(result.pct || 0);
    } catch(_) {}
  }, [user, companyProp, features]);

  // Try to use succession theme context if available, otherwise use local state
  let theme, isDarkTheme, company;
  if (useSuccessionTheme) {
    try {
      const themeContext = useSuccessionTheme();
      theme = themeContext.theme;
      isDarkTheme = themeContext.isDarkTheme;
      company = { ...company, ...(themeContext.company || companyProp || {}) };
    } catch (error) {
      console.error('Header: Failed to use succession theme:', error);
      // Fallback to local state
      theme = localTheme;
      isDarkTheme = localIsDarkTheme;
      company = companyProp;
    }
  } else {
    console.log('Header: useSuccessionTheme not available, using local state');
    // Use local state when theme provider is not available
    theme = localTheme;
    isDarkTheme = localIsDarkTheme;
    company = companyProp;
  }

  // Helper function to convert hex to rgba
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Create theme-aware color system
  const primaryColor = company?.primary_color || '#5489B6';
  const themeColors = {
    bgPrimary: isDarkTheme ? '#0f172a' : '#ffffff',
    bgSecondary: isDarkTheme ? '#1e293b' : '#f8fafc',
    textPrimary: isDarkTheme ? '#f8fafc' : '#0f172a',
    textSecondary: isDarkTheme ? '#cbd5e1' : '#475569',
    textMuted: isDarkTheme ? '#94a3b8' : '#64748b',
    border: isDarkTheme ? '#64748b' : '#e5e7eb',
    borderHover: isDarkTheme ? '#94a3b8' : '#64748b',
    primary: primaryColor,
    focusRing: hexToRgba(primaryColor, 0.5),
  };

  // State to control dropdown visibility
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // State to control notifications drawer visibility
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  // Local override so avatar can update immediately after profile edit save.
  const [avatarFilenameOverride, setAvatarFilenameOverride] = useState(null);
  
  // (Search removed)
  
  // Ref for the dropdown container
  const dropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  
  // Check if current path is the dashboard
  const isDashboardPage = window.location.pathname === '/succession' || 
                         window.location.pathname === '/succession/dashboard';
  
  // Check if modernization feature is enabled
  const isModernizationEnabled = () => {
    return features && features.includes('findbob_modernization');
  };
  
  // Get the user name from available properties
  const getDisplayName = () => {
    if (!user) return 'Guest';
    return user.name || user.user_name || 'User';
  };

  const getAvatarFilename = () => {
    // Support both new avatar system and legacy system
    if (!user) return 'advisor-square.png';
    // Check for new avatar system first (custom_avatar_filename field)
    if (user.custom_avatar_filename) {
      return user.custom_avatar_filename;
    }
    // Legacy: check if profile_picture is a string
    if (typeof user.profile_picture === 'string') {
      return user.profile_picture;
    }
    // Legacy: check nested avatar object - use the filename and add -rectangle.png suffix
    if (user.profile_picture?.avatar?.filename) {
      return `${user.profile_picture.avatar.filename}-rectangle.png`;
    }
    return 'advisor-square.png';
  };

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Toggle notifications drawer visibility
  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  // Close notifications drawer
  const closeNotifications = () => {
    setIsNotificationsOpen(false);
  };
  
  // (Search removed)
  
  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inMenu = userMenuRef.current && userMenuRef.current.contains(event.target);
      if (!inTrigger && !inMenu) setIsDropdownOpen(false);
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside, true);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isDropdownOpen]);

  // Listen for avatar updates emitted by profile edit flows.
  useEffect(() => {
    const handleAvatarUpdated = (event) => {
      const nextFilename = event?.detail?.filename;
      if (nextFilename) setAvatarFilenameOverride(nextFilename);
    };

    window.addEventListener('findbob:avatar-updated', handleAvatarUpdated);
    return () => window.removeEventListener('findbob:avatar-updated', handleAvatarUpdated);
  }, []);

  // Get avatar URL and background color in one call
  const avatarFilename = avatarFilenameOverride || getAvatarFilename();
  const avatar = getAvatarStyle(avatarFilename, {
    ...avatarSettings,
    primary_color: company?.primary_color,
  });

  // Just use a direct div with background image instead of a complex structure
  const avatarStyle = {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    border: '2px solid #4b5563',
    backgroundColor: avatar.backgroundColor,
    backgroundImage: `url(${avatar.url})`,
    backgroundSize: avatar.isNewStyle ? '85%' : 'cover',
    backgroundPosition: avatar.isNewStyle ? 'bottom center' : 'center',
    backgroundRepeat: 'no-repeat',
    display: 'block',
    overflow: 'hidden',
    cursor: 'pointer'
  };

  return (
    <header
      className="relative flex items-center justify-between pl-16 pr-4 lg:px-4 py-2 border-b shadow-sm transition-colors duration-300 bg-succession-bg-primary border-succession-border"
    >
      <div className="flex items-center flex-1 min-w-0">
        {logoLight && (
          <a
            href="/succession/home"
            aria-label="Home"
            className="block flex-shrink-0 mr-4"
          >
            <img
              src={isDarkTheme ? (logoDark || logoLight) : logoLight}
              data-light-logo={logoLight}
              data-dark-logo={logoDark || logoLight}
              alt={company?.branded_name || company?.name || 'FindBob'}
              className="h-8 lg:h-12 w-auto transition-all duration-300"
            />
          </a>
        )}
        {/* Search removed */}
      </div>

      <div className="flex items-center header-hide-on-intake gap-2 pl-3">
        {/* App launcher and Getting Started */}
      <div className="flex items-center gap-2" data-tutorial-id="app-launcher-trigger">
          {/* Getting Started progress pill with donut — hidden on narrow viewports
              where the hamburger drawer surfaces Getting Started instead */}
          {gsPercent < 100 && (
            <button
              onClick={() => setIsGsOpen(true)}
              className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full shadow-succession-sm hover:opacity-90 transition text-xs"
              style={{ backgroundColor: themeColors.primary, color: '#ffffff' }}
              title="Complete Setup"
            >
              <ProgressDonut percent={gsPercent} size={16} stroke={2} />
              <span className="font-semibold">{intl.formatMessage({ id: 'succession.header.complete_setup', defaultMessage: 'Complete setup' })}</span>
              <ChevronRightIcon className="h-3.5 w-3.5" />
            </button>
          )}
          {/* App Launcher - only shown if app_launcher feature is enabled */}
          {features && features.includes('app_launcher') && <AppLauncher />}
        </div>

        {/* Notifications - Use succession system if modernization enabled */}
        {isModernizationEnabled() ? (
          <SuccessionNotificationsContainer
            isOpen={isNotificationsOpen}
            onToggle={toggleNotifications}
            onClose={closeNotifications}
          />
        ) : (
          <button className="flex text-gray-600 focus:outline-none">
            <BellIcon className="h-5 w-5" />
          </button>
        )}
        <ThemeToggle size="small" />
        
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-2 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-300"
            style={{
              color: themeColors.textSecondary,
              focusRingColor: themeColors.focusRing
            }}
          >
            <div style={avatarStyle}></div>
            <span 
              className="text-sm font-medium transition-colors duration-300"
              style={{ color: themeColors.textPrimary }}
            >
              {getDisplayName()}
            </span>
          </button>

          {isDropdownOpen && ReactDOM.createPortal((
            <div
              ref={userMenuRef}
              className="w-48 rounded-md shadow-lg py-1 transition-colors duration-300"
              style={{
                position: 'fixed',
                top: Math.round(((typeof document !== 'undefined' && document.querySelector('header')?.getBoundingClientRect()?.bottom) || 64) + 8),
                right: 24,
                maxHeight: `calc(100vh - ${Math.round(((typeof document !== 'undefined' && document.querySelector('header')?.getBoundingClientRect()?.bottom) || 64) + 8 + 16)}px)`,
                overflowY: 'auto',
                backgroundColor: themeColors.bgPrimary,
                border: `1px solid ${themeColors.border}`,
                zIndex: (typeof document !== 'undefined' && document.body?.classList?.contains('guided-tour-open')) ? 1900 : 2147483647
              }}
              data-user-menu="1"
            >
            <div className="px-4 py-3 border-b border-gray-100">
               {/* Show avatar and name in dropdown */}
               <div>
                  <div style={avatarStyle}></div>
                  <span 
                    className="text-sm font-medium transition-colors duration-300"
                    style={{ color: themeColors.textPrimary }}
                  >
                    {getDisplayName()}
                  </span>
                </div>
              </div>
              <a href={hasCoachProfile ? "/coach/profile/settings" : "/succession/profile"}
                className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                style={{ color: themeColors.textSecondary }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeColors.bgSecondary;
                  e.target.style.color = themeColors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = themeColors.textSecondary;
                }}
              >
                <UserIcon className="mr-3 h-4 w-4" />
                <FormattedMessage id="succession.header.profile" defaultMessage="Profile" />
              </a>
              {/* Coach Portal link - only show for coaches */}
              {hasCoachProfile ? (
                <a
                  href="/coach/portal"
                  className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                  style={{ color: themeColors.textSecondary }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = themeColors.bgSecondary;
                    e.target.style.color = themeColors.textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = themeColors.textSecondary;
                  }}
                >
                  <UserCircleIcon className="mr-3 h-4 w-4" />
                  <FormattedMessage id="succession.header.coach_portal" defaultMessage="Coach Portal" />
                </a>
              ) : null}
              {/* Admin link - only show for admin or customer users */}
              {(user?.admin || user?.customer) ? (
                <a
                  href="/admin/dashboard"
                  className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                  style={{ color: themeColors.textSecondary }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = themeColors.bgSecondary;
                    e.target.style.color = themeColors.textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = themeColors.textSecondary;
                  }}
                >
                  <PresentationChartBarIcon className="mr-3 h-4 w-4" />
                  <FormattedMessage id="succession.header.admin" defaultMessage="Admin" />
                </a>
              ) : null}
              <a
                href="/succession/settings"
                className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                style={{ color: themeColors.textSecondary }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeColors.bgSecondary;
                  e.target.style.color = themeColors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = themeColors.textSecondary;
                }}
              >
                <Cog6ToothIcon className="mr-3 h-4 w-4" />
                <FormattedMessage id="succession.header.settings" defaultMessage="Settings" />
              </a>
              <a
                href="/succession/help"
                className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                style={{ color: themeColors.textSecondary }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeColors.bgSecondary;
                  e.target.style.color = themeColors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = themeColors.textSecondary;
                }}
              >
                <QuestionMarkCircleIcon className="mr-3 h-4 w-4" />
                <FormattedMessage id="succession.header.help" defaultMessage="Help" />
              </a>
              {/* Integrations link - only show when quickbooks_connector feature is enabled */}
              {quickbooksConnectorEnabled && (
                <a
                  href="/succession/integrations"
                  className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                  style={{ color: themeColors.textSecondary }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = themeColors.bgSecondary;
                    e.target.style.color = themeColors.textPrimary;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = themeColors.textSecondary;
                  }}
                >
                  <svg className="mr-3 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Integrations
                </a>
              )}
              {/* Demo Menu - only show for users with demo_secret_menu feature */}
              {demoSecretMenuEnabled && (
                <>
                  <div style={{ borderTop: `1px solid ${themeColors.border}`, margin: '4px 0' }} />
                  <div className="px-4 py-1.5">
                    <span className="flex items-center text-xs font-semibold uppercase tracking-wider" style={{ color: themeColors.textMuted }}>
                      <BeakerIcon className="mr-1.5 h-3.5 w-3.5" />
                      Demo Menu
                    </span>
                  </div>
                  {/* Scenarios submenu (top of Demo Menu for quick access) */}
                  <button
                    onClick={() => setDemoScenariosExpanded(!demoScenariosExpanded)}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm transition-colors duration-300 text-left"
                    style={{ color: themeColors.textSecondary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = themeColors.bgSecondary;
                      e.currentTarget.style.color = themeColors.textPrimary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = themeColors.textSecondary;
                    }}
                  >
                    <span className="flex items-center">
                      <RocketLaunchIcon className="mr-3 h-4 w-4" />
                      Scenarios
                    </span>
                    <ChevronRightIcon
                      className="h-3.5 w-3.5 transition-transform duration-200"
                      style={{ transform: demoScenariosExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </button>
                  {demoScenariosExpanded && (
                    <div style={{ backgroundColor: themeColors.bgSecondary }}>
                      {DEMO_SCENARIOS.map((scenario) => (
                        <button
                          key={scenario.key}
                          onClick={() => setScenarioConfirm(scenario)}
                          disabled={resetLoading || seedLoading}
                          className="flex items-center w-full pl-8 pr-4 py-1.5 text-sm transition-colors duration-300 text-left"
                          style={{ color: themeColors.textSecondary, opacity: (resetLoading || seedLoading) ? 0.5 : 1 }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = themeColors.border;
                            e.currentTarget.style.color = themeColors.textPrimary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = themeColors.textSecondary;
                          }}
                        >
                          <RocketLaunchIcon className="mr-3 h-3.5 w-3.5" />
                          {scenario.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ borderTop: `1px solid ${themeColors.border}`, margin: '2px 12px' }} />
                  <button
                    onClick={() => setDemoResetExpanded(!demoResetExpanded)}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm transition-colors duration-300 text-left"
                    style={{ color: themeColors.textSecondary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = themeColors.bgSecondary;
                      e.currentTarget.style.color = themeColors.textPrimary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = themeColors.textSecondary;
                    }}
                  >
                    <span className="flex items-center">
                      <ArrowPathIcon className="mr-3 h-4 w-4" />
                      Reset
                    </span>
                    <ChevronRightIcon
                      className="h-3.5 w-3.5 transition-transform duration-200"
                      style={{ transform: demoResetExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </button>
                  {demoResetExpanded && (
                    <div style={{ backgroundColor: themeColors.bgSecondary }}>
                      {DEMO_RESET_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          onClick={() => setDemoConfirm(option)}
                          disabled={resetLoading}
                          className="flex items-center w-full pl-8 pr-4 py-1.5 text-sm transition-colors duration-300 text-left"
                          style={{ color: option.key === 'all' ? '#ef4444' : themeColors.textSecondary, opacity: resetLoading ? 0.5 : 1 }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = themeColors.border;
                            e.currentTarget.style.color = option.key === 'all' ? '#dc2626' : themeColors.textPrimary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = option.key === 'all' ? '#ef4444' : themeColors.textSecondary;
                          }}
                        >
                          <ArrowPathIcon className="mr-3 h-3.5 w-3.5" />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {/* Seed submenu */}
                  <button
                    onClick={() => setDemoSeedExpanded(!demoSeedExpanded)}
                    className="flex items-center justify-between w-full px-4 py-2 text-sm transition-colors duration-300 text-left"
                    style={{ color: themeColors.textSecondary }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = themeColors.bgSecondary;
                      e.currentTarget.style.color = themeColors.textPrimary;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = themeColors.textSecondary;
                    }}
                  >
                    <span className="flex items-center">
                      <PresentationChartBarIcon className="mr-3 h-4 w-4" />
                      Seed
                    </span>
                    <ChevronRightIcon
                      className="h-3.5 w-3.5 transition-transform duration-200"
                      style={{ transform: demoSeedExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </button>
                  {demoSeedExpanded && (
                    <div style={{ backgroundColor: themeColors.bgSecondary }}>
                      {DEMO_SEED_OPTIONS.map((option) => (
                        <button
                          key={option.key}
                          onClick={async () => {
                            if (option.requiresMotivation && user.motivation !== option.requiresMotivation) {
                              setDemoStatus({ type: 'error', message: `This action is only available for ${option.requiresMotivation}-motivated users. Current motivation: ${user.motivation || 'not set'}.` });
                              setTimeout(() => setDemoStatus(null), 5000);
                            } else if (option.needsSkipLastInput) {
                              // Fetch total questions before showing the dialog
                              try {
                                const templateSlug = option.templateSlug || 'comprehensive-succession-plan-v2';
                                const result = await apolloClient.query({ query: PLAN_TEMPLATE_TOTAL_QUERY, variables: { slug: templateSlug }, fetchPolicy: 'network-only' });
                                const total = result?.data?.planTemplate?.total_questions || 0;
                                setMidPlanTotalQuestions(total);
                                setMidPlanSkipLast(total > 1 ? String(Math.floor(total / 2)) : '0');
                                setDemoSeedConfirm(option);
                              } catch (err) {
                                setDemoStatus({ type: 'error', message: `Could not load plan template: ${err.message}` });
                                setTimeout(() => setDemoStatus(null), 5000);
                              }
                            } else {
                              setDemoSeedConfirm(option);
                            }
                          }}
                          disabled={seedLoading}
                          className="flex items-center w-full pl-8 pr-4 py-1.5 text-sm transition-colors duration-300 text-left"
                          style={{ color: themeColors.textSecondary, opacity: seedLoading ? 0.5 : 1 }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = themeColors.border;
                            e.currentTarget.style.color = themeColors.textPrimary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = themeColors.textSecondary;
                          }}
                        >
                          <PresentationChartBarIcon className="mr-3 h-3.5 w-3.5" />
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <a
                href="/logout"
                className="flex items-center px-4 py-2 text-sm transition-colors duration-300"
                style={{ color: themeColors.textSecondary }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = themeColors.bgSecondary;
                  e.target.style.color = themeColors.textPrimary;
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = themeColors.textSecondary;
                }}
              >
                <ArrowRightOnRectangleIcon className="mr-3 h-4 w-4" />
                <FormattedMessage id="succession.header.sign_out" defaultMessage="Sign out" />
              </a>
            </div>
          ), document.body)}
        </div>
      </div>
      <GettingStartedDrawer open={isGsOpen} onClose={() => setIsGsOpen(false)} current_user={user} company={company} features={features} />
      
      {/* Demo Reset Confirmation Dialog */}
      {demoConfirm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2147483647,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setDemoConfirm(null)}
        >
          <div
            style={{
              backgroundColor: themeColors.bgPrimary,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '8px', padding: '24px', maxWidth: '400px', width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2" style={{ color: themeColors.textPrimary }}>
              Confirm {demoConfirm.label}
            </h3>
            <p className="text-sm mb-6" style={{ color: themeColors.textSecondary }}>
              Are you sure you want to {demoConfirm.label.toLowerCase()}? This action cannot be undone.
              {demoConfirm.key === 'all' && ' This will reset ALL demo data including onboarding, valuations, financing progress, deals, plans, the readiness assessment, cash flows, conversations, and rolodex.'}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDemoConfirm(null)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{
                  color: themeColors.textSecondary,
                  backgroundColor: themeColors.bgSecondary,
                  border: `1px solid ${themeColors.border}`
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDemoReset(demoConfirm.key)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{
                  color: '#ffffff',
                  backgroundColor: demoConfirm.key === 'all' ? '#ef4444' : themeColors.primary
                }}
              >
                {demoConfirm.key === 'all' ? 'Reset Everything' : 'Confirm Reset'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Demo Seed Confirmation Dialog */}
      {demoSeedConfirm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2147483647,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setDemoSeedConfirm(null)}
        >
          <div
            style={{
              backgroundColor: themeColors.bgPrimary,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '8px', padding: '24px', maxWidth: '440px', width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2" style={{ color: themeColors.textPrimary }}>
              Confirm {demoSeedConfirm.label}
            </h3>
            <p className="text-sm mb-4" style={{ color: themeColors.textSecondary }}>
              {demoSeedConfirm.description || `Are you sure you want to ${demoSeedConfirm.label.toLowerCase()}?`}
            </p>

            {/* Mid-plan skip_last input */}
            {demoSeedConfirm.needsSkipLastInput && midPlanTotalQuestions != null && (
              <div className="mb-4" style={{ padding: '12px', borderRadius: '6px', backgroundColor: themeColors.bgSecondary, border: `1px solid ${themeColors.border}` }}>
                <p className="text-xs mb-2" style={{ color: themeColors.textSecondary }}>
                  Total questions in plan: <strong style={{ color: themeColors.textPrimary }}>{midPlanTotalQuestions}</strong>
                </p>
                <label className="text-sm font-medium block mb-1" style={{ color: themeColors.textPrimary }}>
                  Number of questions to skip (from end):
                </label>
                <input
                  type="number"
                  min="0"
                  max={midPlanTotalQuestions - 1}
                  value={midPlanSkipLast}
                  onChange={(e) => setMidPlanSkipLast(e.target.value)}
                  className="w-full rounded-md text-sm"
                  style={{
                    padding: '8px 12px',
                    backgroundColor: themeColors.bgPrimary,
                    color: themeColors.textPrimary,
                    border: `1px solid ${themeColors.border}`,
                    outline: 'none'
                  }}
                />
                <p className="text-xs mt-1" style={{ color: themeColors.textSecondary }}>
                  This will answer <strong>{midPlanTotalQuestions - (parseInt(midPlanSkipLast, 10) || 0)}</strong> of {midPlanTotalQuestions} questions.
                  Valid range: 0 to {midPlanTotalQuestions - 1}.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setDemoSeedConfirm(null); setMidPlanSkipLast(''); setMidPlanTotalQuestions(null); }}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{
                  color: themeColors.textSecondary,
                  backgroundColor: themeColors.bgSecondary,
                  border: `1px solid ${themeColors.border}`
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (demoSeedConfirm.needsSkipLastInput) {
                    const val = parseInt(midPlanSkipLast, 10);
                    if (isNaN(val) || val < 0 || val >= midPlanTotalQuestions) {
                      setDemoStatus({ type: 'error', message: `Skip count must be between 0 and ${midPlanTotalQuestions - 1}.` });
                      setTimeout(() => setDemoStatus(null), 5000);
                      return;
                    }
                    handleDemoSeed(demoSeedConfirm, { skipLast: val });
                  } else {
                    handleDemoSeed(demoSeedConfirm);
                  }
                }}
                disabled={demoSeedConfirm.needsSkipLastInput && (() => {
                  const val = parseInt(midPlanSkipLast, 10);
                  return isNaN(val) || val < 0 || val >= (midPlanTotalQuestions || 1);
                })()}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{
                  color: '#ffffff',
                  backgroundColor: themeColors.primary
                }}
              >
                Confirm Seed
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Scenario Confirmation Dialog */}
      {scenarioConfirm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2147483647,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          onClick={() => setScenarioConfirm(null)}
        >
          <div
            style={{
              backgroundColor: themeColors.bgPrimary,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '12px', padding: '28px', maxWidth: '520px', width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-3">
              <div style={{ backgroundColor: hexToRgba(themeColors.primary, 0.1), borderRadius: '10px', padding: '8px' }}>
                <RocketLaunchIcon className="h-5 w-5" style={{ color: themeColors.primary }} />
              </div>
              <h3 className="text-lg font-semibold" style={{ color: themeColors.textPrimary }}>
                {scenarioConfirm.label}
              </h3>
            </div>
            <p className="text-sm mb-4" style={{ color: themeColors.textSecondary }}>
              {scenarioConfirm.description}
            </p>

            <div style={{ backgroundColor: themeColors.bgSecondary, borderRadius: '8px', border: `1px solid ${themeColors.border}`, padding: '16px', marginBottom: '16px' }}>
              <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: themeColors.textMuted }}>
                This scenario will execute:
              </p>
              {scenarioConfirm.steps.map((step, idx) => (
                <div key={idx} className="flex items-start gap-3" style={{ marginBottom: idx < scenarioConfirm.steps.length - 1 ? '10px' : 0 }}>
                  <span
                    className="flex-shrink-0 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center"
                    style={{ backgroundColor: themeColors.primary, color: '#fff', marginTop: '1px' }}
                  >
                    {idx + 1}
                  </span>
                  <div>
                    <p className="text-sm font-medium" style={{ color: themeColors.textPrimary }}>{step.label}</p>
                    {step.description && (
                      <p className="text-xs mt-0.5" style={{ color: themeColors.textMuted }}>{step.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '6px', padding: '10px 12px', marginBottom: '20px' }}>
              <p className="text-xs" style={{ color: '#92400e' }}>
                <strong>Note:</strong> This process may take 2–3 minutes. The page will reload automatically when complete. Do not navigate away.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setScenarioConfirm(null)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors"
                style={{
                  color: themeColors.textSecondary,
                  backgroundColor: themeColors.bgSecondary,
                  border: `1px solid ${themeColors.border}`
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => executeScenario(scenarioConfirm)}
                className="px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2"
                style={{
                  color: '#ffffff',
                  backgroundColor: themeColors.primary
                }}
              >
                <RocketLaunchIcon className="h-4 w-4" />
                Execute Scenario
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Demo Operation In Progress Overlay */}
      {demoOperationInProgress && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 2147483647,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(2px)'
          }}
        >
          <div
            style={{
              backgroundColor: themeColors.bgPrimary,
              border: `1px solid ${themeColors.border}`,
              borderRadius: '12px', padding: '32px 40px',
              textAlign: 'center', maxWidth: '400px', width: '90%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
            }}
          >
            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
              <svg
                style={{ animation: 'demo-spin 1s linear infinite', width: '40px', height: '40px', color: themeColors.primary }}
                xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              >
                <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <p className="text-sm font-medium" style={{ color: themeColors.textPrimary, marginBottom: scenarioProgress ? '12px' : '4px' }}>
              {demoOperationInProgress.message}
            </p>

            {/* Scenario step checklist */}
            {scenarioProgress && (
              <div style={{ textAlign: 'left', width: '100%', marginBottom: '12px' }}>
                {scenarioProgress.steps.map((step, idx) => (
                  <div key={idx} className="flex items-center gap-2" style={{ marginBottom: idx < scenarioProgress.steps.length - 1 ? '8px' : 0 }}>
                    {step.status === 'done' ? (
                      <CheckCircleIcon className="h-4 w-4 flex-shrink-0" style={{ color: '#10b981' }} />
                    ) : step.status === 'running' ? (
                      <svg
                        className="h-4 w-4 flex-shrink-0"
                        style={{ animation: 'demo-spin 1s linear infinite', color: themeColors.primary }}
                        xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                      >
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : step.status === 'error' ? (
                      <div className="h-4 w-4 rounded-full flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: '#fecaca' }}>
                        <span style={{ color: '#ef4444', fontSize: '10px', fontWeight: 700, lineHeight: 1 }}>!</span>
                      </div>
                    ) : (
                      <div className="h-4 w-4 rounded-full flex-shrink-0" style={{ border: `2px solid ${themeColors.border}` }} />
                    )}
                    <span
                      className="text-xs"
                      style={{
                        color: step.status === 'done' ? '#10b981' : step.status === 'running' ? themeColors.textPrimary : step.status === 'error' ? '#ef4444' : themeColors.textMuted,
                        fontWeight: step.status === 'running' ? 600 : 400,
                      }}
                    >
                      {step.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <p className="text-xs" style={{ color: themeColors.textMuted }}>
              {scenarioProgress ? 'Scenarios typically take 2–3 minutes. Please do not navigate away.' : 'Deal room seeds typically take 1–2 minutes. Please do not navigate away.'}
            </p>
          </div>
          <style>{`@keyframes demo-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>,
        document.body
      )}

      {/* Demo Status Toast (errors only, success handled by overlay) */}
      {demoStatus && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed', top: '80px', right: '24px', zIndex: 2147483647,
            padding: '12px 20px', borderRadius: '8px', maxWidth: '360px',
            backgroundColor: demoStatus.type === 'success' ? '#10b981' : '#ef4444',
            color: '#ffffff', fontSize: '14px', fontWeight: 500,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)'
          }}
        >
          {demoStatus.message}
        </div>,
        document.body
      )}

      {/* Search styles removed */}
    </header>
  );
};

export default Header; 